import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';

/**
 * One pre-aggregated chart bar. Monthly bars come straight from
 * water_saved_monthly_summary rows; quarter/year bars from the server-side
 * period rollups — the chart never aggregates.
 */
export interface WaterSavedChartBucket {
  /** Stable sort/select key: "2026-03" | "2026-01-01" */
  key: string;
  /** Display label: "Mar 26" | "Q1 2026" | "2026" */
  label: string;
  /** False when the period is missing months of data (partial) */
  isComplete: boolean;
  totalGallonsSaved: number;
  fixCount: number;
  avgGallonsSaved: number;
  avgPctSaved: number;
}

interface WaterSavedMonthlyChartProps {
  data: WaterSavedChartBucket[];
  /** Noun for titles/hints: "Month" | "Quarter" | "Year" */
  periodNoun: string;
  /** Label for the fix count in the tooltip ("Fixed Leaks" | "Leak Fixes") */
  fixCountLabel: string;
  onBucketClick?: (key: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

interface TooltipPayload {
  dataKey: string;
  color: string;
  value: number;
  payload: WaterSavedChartBucket & { fixCountLabel: string };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      {/* label already carries the "(partial)" suffix for incomplete periods */}
      <p className='font-semibold mb-2'>{data.label}</p>
      <div className='space-y-1 text-sm'>
        <p>
          Total Saved:{' '}
          <span className='font-semibold'>{data.totalGallonsSaved.toLocaleString()} gal</span>
        </p>
        <p>
          {data.fixCountLabel}: <span className='font-semibold'>{data.fixCount}</span>
        </p>
        <p>
          Avg per Fix:{' '}
          <span className='font-semibold'>{data.avgGallonsSaved.toLocaleString()} gal</span>
        </p>
        <p>
          Avg % Saved: <span className='font-semibold'>{data.avgPctSaved.toFixed(1)}%</span>
        </p>
      </div>
    </div>
  );
}

export function WaterSavedMonthlyChart({
  data,
  periodNoun,
  fixCountLabel,
  onBucketClick,
  isLoading,
  error,
}: WaterSavedMonthlyChartProps) {
  // Sort ascending by key for the chart (oldest to newest)
  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((bucket) => ({ ...bucket, fixCountLabel }));
  }, [data, fixCountLabel]);

  // Calculate total gallons saved
  const totalSaved = useMemo(() => {
    return data.reduce((sum, item) => sum + item.totalGallonsSaved, 0);
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Water Saved by {periodNoun}</CardTitle>
        <CardDescription>
          Total gallons saved from fixed leaks
          {totalSaved > 0 && ` • ${totalSaved.toLocaleString()} gallons total`}
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {onBucketClick && chartData.length > 0 && (
          <p className='text-sm text-muted-foreground'>
            <span className='font-medium text-foreground'>Click on a bar</span>{' '}
            to view detailed meter data for that {periodNoun.toLowerCase()}.
          </p>
        )}
        {isLoading && (
          <div className='flex items-center justify-center h-[300px]'>
            <p className='text-sm text-muted-foreground'>Loading chart data...</p>
          </div>
        )}

        {error && (
          <div className='flex items-center justify-center h-[300px]'>
            <p className='text-sm text-destructive'>Failed to load chart data</p>
          </div>
        )}

        {!isLoading && !error && chartData.length === 0 && (
          <div className='flex flex-col items-center justify-center h-[300px] text-center'>
            <p className='text-sm text-muted-foreground'>No water saved data available</p>
            <p className='text-xs text-muted-foreground mt-1'>
              Compute data for a month to see results here
            </p>
          </div>
        )}

        {!isLoading && !error && chartData.length > 0 && (
          <div className='[&_svg]:outline-none [&_svg:focus]:outline-none'>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={chartData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis
                  dataKey='label'
                  tick={{ fontSize: 11 }}
                  tickMargin={8}
                  interval='preserveStartEnd'
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  width={70}
                  tickFormatter={(value: number) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey='totalGallonsSaved'
                  fill='hsl(var(--primary))'
                  radius={[4, 4, 0, 0]}
                  cursor={onBucketClick ? 'pointer' : 'default'}
                  activeBar={{ stroke: 'none', outline: 'none' }}
                  onClick={(data: { key: string }) => {
                    if (onBucketClick && data?.key) {
                      onBucketClick(data.key);
                    }
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
